import type { ChangeEvent, KeyboardEvent, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { WastageSwitch } from './product-details-wastage-switch';

interface QuantityInputProps {
  label: string;
  unit?: string;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  className?: string;
  maxQuantity?: number;
  isStatic?: boolean;
  buttonClassName?: string;
}

function QuantityInput({
  label,
  unit,
  quantity,
  onQuantityChange,
  className,
  maxQuantity,
  isStatic = false,
  buttonClassName = 'flex items-center justify-center px-1.5 py-1 hf:text-primary/80',
}: QuantityInputProps) {
  const [inputValue, setInputValue] = useState(
    unit ? quantity.toFixed(2) : quantity.toString()
  );

  useEffect(() => {
    setInputValue(unit ? quantity.toFixed(2) : quantity.toString());
  }, [quantity, unit]);

  const handleQuantityChange = (newQuantity: number) => {
    if (isStatic) return;

    const minQuantity = 1;
    const maxAllowed = maxQuantity ?? Number.POSITIVE_INFINITY;
    const validQuantity = Math.max(
      minQuantity,
      Math.min(newQuantity, maxAllowed)
    );
    onQuantityChange(validQuantity);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (isStatic) return;
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    if (isStatic) return;

    const numValue = Number(inputValue);
    if (!Number.isNaN(numValue) && numValue > 0) {
      handleQuantityChange(numValue);
    } else {
      setInputValue(unit ? quantity.toFixed(2) : quantity.toString());
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (isStatic) return;

    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  const isAtMaxQuantity = maxQuantity !== undefined && quantity >= maxQuantity;

  return (
    <div className={className}>
      <span className="flex-1 font-semibold">{label}</span>
      <button
        type="button"
        className={buttonClassName}
        onClick={() => handleQuantityChange(quantity - 1)}
        disabled={isStatic}
      >
        <span className="sr-only">Decrease quantity by 1</span>
        <svg
          viewBox="0 0 12 3"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="h-2.5 w-2.5"
        >
          <title>Decrease quantity by 1</title>
          <path
            d="M1.3335 1.5H10.6668"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <label>
        <span className="sr-only">{label}</span>
        <input
          type="string"
          inputMode="numeric"
          name="quantity"
          min="1.00"
          step="1.00"
          max={maxQuantity}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          readOnly={isStatic}
          className="w-12 appearance-none bg-inherit text-center font-semibold outline-none"
        />
      </label>
      {unit && (
        <div className="font-semibold">
          {unit === 'M²' ? (
            <>
              M<sup>2</sup>
            </>
          ) : (
            unit
          )}
        </div>
      )}
      <button
        type="button"
        className={`${buttonClassName} disabled:opacity-50`}
        onClick={() => handleQuantityChange(quantity + 1)}
        disabled={isStatic || isAtMaxQuantity}
      >
        <span className="sr-only">Increase quantity by 1</span>
        <svg
          viewBox="0 0 12 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="h-2.5 w-2.5"
        >
          <title>Increase quantity by 1</title>
          <path
            d="M6.00019 1.83331V11.1666M1.3335 6.49998H10.6668"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
}

interface WastageSwitchShellProps {
  labelClassName?: string;
  switchClassName?: string;
  thumbClassName?: string;
  isStatic?: boolean;
}

function WastageSwitchShell({
  labelClassName,
  switchClassName,
  thumbClassName,
  isStatic = false,
}: WastageSwitchShellProps) {
  return (
    <WastageSwitch>
      <WastageSwitch.Label className={labelClassName}>
        <span className="max-sm:hidden">Please</span> add 15% wastage
      </WastageSwitch.Label>
      {isStatic ? (
        <div className={switchClassName}>
          <div className={thumbClassName} />
        </div>
      ) : (
        <WastageSwitch.Toggle
          className={switchClassName}
          thumbClassName={thumbClassName}
        />
      )}
    </WastageSwitch>
  );
}

interface ProductQuantityShellProps {
  productType: 'TILE' | string;
  squareMetersPerBox: number | null;
  isStatic?: boolean;
  className?: string;
  containerClassName?: string;
  quantityInputClassName?: string;
  buttonClassName?: string;
  hintClassName?: string;
  quantity?: number;
  maxQuantity?: number;
  onQuantityChange?: (quantity: number) => void;
  wastageSwitch?: ReactNode;
  wastageSwitchProps?: {
    labelClassName?: string;
    switchClassName?: string;
    thumbClassName?: string;
  };
}

export function ProductQuantityShell({
  productType,
  squareMetersPerBox,
  isStatic = false,
  className = 'col-span-full',
  containerClassName = 'grid-cols-2 gap-[--product-grid-gap] max-sm:flex sm:grid',
  quantityInputClassName,
  buttonClassName,
  hintClassName,
  quantity = 1,
  maxQuantity,
  // biome-ignore lint/suspicious/noEmptyBlockStatements: <explanation>
  onQuantityChange = () => {},
  wastageSwitch,
  wastageSwitchProps,
}: ProductQuantityShellProps) {
  const isTile = productType === 'TILE';

  return (
    <div data-product-block="quantity_selector" className={className}>
      <div className={containerClassName}>
        {isTile && squareMetersPerBox ? (
          <QuantityInput
            label="Area"
            unit="M²"
            quantity={quantity}
            onQuantityChange={onQuantityChange}
            className={quantityInputClassName}
            maxQuantity={maxQuantity}
            isStatic={isStatic}
            buttonClassName={buttonClassName}
          />
        ) : (
          <QuantityInput
            label="Quantity"
            quantity={quantity}
            onQuantityChange={onQuantityChange}
            className={quantityInputClassName}
            maxQuantity={maxQuantity}
            isStatic={isStatic}
            buttonClassName={buttonClassName}
          />
        )}
        {isTile && wastageSwitch}
        {isTile && !wastageSwitch && wastageSwitchProps && (
          <WastageSwitchShell
            isStatic={isStatic}
            labelClassName={wastageSwitchProps.labelClassName}
            switchClassName={wastageSwitchProps.switchClassName}
            thumbClassName={wastageSwitchProps.thumbClassName}
          />
        )}
      </div>
      {isTile && hintClassName && (
        <small className={hintClassName}>We round up to the full box</small>
      )}
    </div>
  );
}
